"use client";

import { cn } from "@/components/libs/cn";
import { WavyDivider } from "@/components/ui/wavy-divider";
import { default as IconCloud } from "@/features/landing/assets/cloud.svgr";
import { default as IconHat } from "@/features/landing/assets/hat.svgr";
import { default as IconHeart } from "@/features/landing/assets/heart.svgr";
import { default as IconStar } from "@/features/landing/assets/star.svgr";
import { useScrollCallback } from "@/hooks/useOptimizedScroll";
import { useIsDesktop } from "@/shared/useIsDesktop";
import {
  createSeededRandom,
  generateSeedFromString,
} from "@/utils/seededRandom";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface CloudStampsProps {
  iconSize?: number;
  count?: number;
  color?: string;
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  parallaxSpeed?: number;
}

function darkenColor(color?: string, amount = 0.2): string {
  if (!color) return "#000000";

  // Handle hex colors
  if (color.startsWith("#")) {
    const hex = color.slice(1);
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const darkenedR = Math.round(r * (1 - amount));
    const darkenedG = Math.round(g * (1 - amount));
    const darkenedB = Math.round(b * (1 - amount));

    return `#${darkenedR.toString(16).padStart(2, "0")}${darkenedG.toString(16).padStart(2, "0")}${darkenedB.toString(16).padStart(2, "0")}`;
  }

  // Handle rgb/rgba colors
  if (color.startsWith("rgb")) {
    const match = color.match(/\d+/g);
    if (match && match.length >= 3) {
      const r = Math.round(parseInt(match[0] ?? "0") * (1 - amount));
      const g = Math.round(parseInt(match[1] ?? "0") * (1 - amount));
      const b = Math.round(parseInt(match[2] ?? "0") * (1 - amount));
      const a = match[3] ? parseFloat(match[3]) : 1;

      return match[3]
        ? `rgba(${r}, ${g}, ${b}, ${a})`
        : `rgb(${r}, ${g}, ${b})`;
    }
  }

  return color;
}

export function CloudStamps({
  iconSize = 48,
  count,
  color,
  top = "0px",
  left = "0px",
  right = "0px",
  bottom = "0px",
  parallaxSpeed = 2,
}: CloudStampsProps) {
  const isDesktop = useIsDesktop();
  if (count == null) {
    count = isDesktop ? 10 : 4;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const darkerColor = useMemo(() => darkenColor(color), [color]);

  const icons = useMemo(() => [IconHat, IconCloud, IconHeart, IconStar], []);

  const handleScrollChange = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Only apply parallax when section is in view
    if (rect.bottom > 0 && rect.top < windowHeight) {
      // Calculate normalized progress (-1 to 1)
      // -1: section just entered from bottom
      // 0: section is centered in viewport
      // 1: section is about to exit from top
      const sectionHeight = rect.height;
      const sectionCenter = rect.top + sectionHeight / 2;
      const viewportCenter = windowHeight / 2;
      const maxDistance = (windowHeight + sectionHeight) / 2;

      const normalizedProgress = (viewportCenter - sectionCenter) / maxDistance;
      const clampedProgress = Math.max(-1, Math.min(1, normalizedProgress));

      setScrollY(clampedProgress);
    }
  }, []);

  const handleResize = useCallback(() => {
    handleScrollChange();
  }, [handleScrollChange]);

  useScrollCallback(handleScrollChange);

  useEffect(() => {
    if (containerRef.current) {
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    handleScrollChange();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleScrollChange, handleResize]);

  const stamps = useMemo(() => {
    if (!isReady || !containerRef.current) return [];

    const seedString = "asdfasdfsadfasdfasdfadf";
    const seed = generateSeedFromString(seedString);
    const rng = createSeededRandom(seed);

    const spacing = iconSize * 2;
    const placedClouds: Array<{ top: number; left: number }> = [];
    const maxAttempts = count * 20;

    // Calculate padding to keep icons within bounds
    const container = containerRef.current;
    const paddingX = (iconSize / 2 / container.clientWidth) * 100;
    const paddingY = (iconSize / 2 / container.clientHeight) * 100;

    // Divide vertical space into sections to avoid same-row placement
    const verticalSections = count;
    const sectionHeight = (100 - 2 * paddingY) / verticalSections;

    // Divide horizontal space into sections to avoid same-column placement
    const horizontalSections = count;
    const sectionWidth = (100 - 2 * paddingX) / horizontalSections;

    function isValidPosition(newTop: number, newLeft: number): boolean {
      if (!containerRef.current) return false;

      const container = containerRef.current;

      return placedClouds.every(({ top, left }) => {
        const distance = Math.sqrt(
          Math.pow(((newTop - top) * container.clientHeight) / 100, 2) +
            Math.pow(((newLeft - left) * container.clientWidth) / 100, 2),
        );
        return distance >= spacing;
      });
    }

    const clouds = [];
    let attempts = 0;
    const usedHorizontalSections = new Set<number>();

    for (let i = 0; i < count && attempts < maxAttempts; attempts++) {
      // Place each stamp in its designated vertical section
      const sectionStart = paddingY + i * sectionHeight;
      const sectionEnd = paddingY + (i + 1) * sectionHeight;
      const top = sectionStart + rng.nextFloat(0, sectionEnd - sectionStart);

      // Select a random horizontal section that hasn't been used
      let horizontalSectionIndex;
      const availableSections = [];
      for (let j = 0; j < horizontalSections; j++) {
        if (!usedHorizontalSections.has(j)) {
          availableSections.push(j);
        }
      }

      if (availableSections.length === 0) {
        // If all sections are used, reset and allow reuse
        usedHorizontalSections.clear();
        horizontalSectionIndex = rng.nextInt(horizontalSections);
      } else {
        const randomIndex = rng.nextInt(availableSections.length);
        const selectedSection = availableSections[randomIndex];
        if (selectedSection === undefined) continue;
        horizontalSectionIndex = selectedSection;
      }

      const horizontalSectionStart =
        paddingX + horizontalSectionIndex * sectionWidth;
      const horizontalSectionEnd =
        paddingX + (horizontalSectionIndex + 1) * sectionWidth;
      const left =
        horizontalSectionStart +
        rng.nextFloat(0, horizontalSectionEnd - horizontalSectionStart);

      if (isValidPosition(top, left)) {
        const cloudData = {
          top,
          left,
        };
        placedClouds.push(cloudData);
        usedHorizontalSections.add(horizontalSectionIndex);
        clouds.push({
          id: i,
          ...cloudData,
          rotation: rng.nextFloat(-22.5, 22.5),
          parallaxOffset: rng.nextFloat(-1, 1),
        });
        i++;
      }
    }

    return clouds;
  }, [count, iconSize, isReady]);

  return (
    <div
      ref={containerRef}
      className="absolute z-0 overflow-hidden text-black"
      style={{
        top,
        left,
        right,
        bottom,
      }}
    >
      {stamps.map((stamp) => {
        const IconComponent = icons[stamp.id % icons.length];
        if (!IconComponent) return null;

        // More subtle parallax movement, centered around original position
        const parallaxY =
          scrollY * parallaxSpeed * 50 * (1 + stamp.parallaxOffset * 0.3);

        return (
          <div
            key={stamp.id}
            className="absolute"
            style={{
              top: `${stamp.top}%`,
              left: `${stamp.left}%`,
              opacity: 0.4,
              transform: `translate(-50%, -50%) rotate(${stamp.rotation}deg) translateY(${parallaxY}px)`,
              color: darkerColor,
            }}
          >
            <IconComponent width={iconSize} height={iconSize} />
          </div>
        );
      })}
    </div>
  );
}

interface GradientSectionProps {
  children: ReactNode;
  topColor: string;
  bottomColor: string;
  className?: string;
  containerClassName?: string;
  stampsCount?: number | false;
  stampsTop?: string;
  stampsLeft?: string;
  stampsRight?: string;
  stampsBottom?: string;
  stampsParallaxSpeed?: number;
  topWave?: string;
  bottomWave?: string;
  topWaveId?: number;
  bottomWaveId?: number;
}

export function GradientSection({
  children,
  topColor,
  bottomColor,
  className,
  containerClassName,
  stampsCount,
  stampsTop,
  stampsLeft,
  stampsRight,
  stampsBottom,
  stampsParallaxSpeed,
  topWave,
  bottomWave,
  topWaveId,
  bottomWaveId,
}: GradientSectionProps) {
  return (
    <div
      className={cn(
        "relative w-full bg-gradient-to-b",
        bottomWave && "pb-[60px]",
        topWave && "pt-[60px]",
        className,
      )}
      style={{
        background: `linear-gradient(to bottom, ${topColor}, ${bottomColor})`,
      }}
    >
      <div className={cn("relative z-[1] w-full", containerClassName)}>
        {children}
      </div>
      {stampsCount !== false && (
        <CloudStamps
          color={topColor}
          count={stampsCount}
          top={stampsTop}
          left={stampsLeft}
          right={stampsRight}
          bottom={stampsBottom}
          parallaxSpeed={stampsParallaxSpeed}
        />
      )}
      {topWave && (
        <WavyDivider
          className="absolute -top-px"
          topColor={topWave}
          bottomColor={bottomColor}
          id={topWaveId}
          direction="right"
        />
      )}
      {bottomWave && (
        <WavyDivider
          flip
          className="absolute -bottom-px"
          topColor={bottomColor}
          bottomColor={bottomWave}
          id={bottomWaveId}
          direction="left"
        />
      )}
    </div>
  );
}
